// Thin fetch wrapper. The API lives at /api on the same origin in production
// and is proxied there by Vite in development, so there is no base URL to
// configure and no CORS to negotiate.

const BASE = '/api'

export class ApiError extends Error {
  // Declared and assigned explicitly rather than as a constructor parameter
  // property, which `erasableSyntaxOnly` disallows.
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }

  /** True when the user is simply not logged in, rather than forbidden. */
  get isUnauthorized() {
    return this.status === 401
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    // Session lives in an httpOnly cookie, so it must be sent explicitly.
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    throw new ApiError(response.status, await extractError(response))
  }

  // 204 No Content, e.g. logout and delete.
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** Pull a human-readable message out of FastAPI's error shapes. */
async function extractError(response: Response): Promise<string> {
  try {
    const data = await response.json()
    const detail = data?.detail

    if (typeof detail === 'string') return detail

    // Validation errors arrive as a list of {loc, msg, type} objects.
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0]
      const field = Array.isArray(first.loc) ? first.loc.at(-1) : null
      return field ? `${field}: ${first.msg}` : first.msg
    }
  } catch {
    // Fall through to the generic message below.
  }
  return `Request failed (${response.status})`
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
}
